import React, {
    useCallback,
    useEffect,
    useRef,
    useState,
    type Ref,
} from "react";
import { SpreadsheetReference } from "@common/schemas/spreadsheetReference.schema";
import { TransactionReference } from "@common/schemas/transactionReference.schema";
import { CounterpartyReference } from "@common/schemas/counterpartyReference.schema";
import { InvoiceReference } from "@common/schemas/invoiceReference.schema";
import { DocumentReference } from "@common/schemas/documentReference.schema";
import {
    FINANCIAL_DOCUMENT_TYPES,
    MAX_FINANCIAL_DOCUMENT_FILE_SIZE_BYTES,
    MAX_FINANCIAL_DOCUMENT_FILE_SIZE_MB,
} from "@common/constants/financial-documents";
import { useScope } from "@state/scopeContext.lib";
import { uploadDocument } from "@lib/financialDocument";
import { toBase64 } from "@helpers/file";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { SlateEditor, type SlateEditorHandle } from "./slate/SlateEditor";
import { isSlateValueEmpty, serializeSlateValue } from "./slate/slateHelpers";
import { SlateValue } from "./slate/slateTypes";
import { fileKey } from "./fileAttachmentUtils";

/** Matches the contract-side max on `financialDocumentIds` */
const MAX_CHAT_ATTACHMENTS = 10;

export interface MessageFormSubmitParams {
    content: string;
    organizationId?: string | null;
    spreadsheetReferences?: SpreadsheetReference[];
    transactionReferences?: TransactionReference[];
    counterpartyReferences?: CounterpartyReference[];
    invoiceReferences?: InvoiceReference[];
    documentReferences?: DocumentReference[];
    financialDocumentIds?: number[];
}

interface MessageFormProps {
    /**
     * Sends the message. May be async — when it rejects, the composer text and
     * attachment chips are preserved so the user can retry without re-typing or
     * re-uploading.
     */
    onSubmit: (params: MessageFormSubmitParams) => void | Promise<void>;
    placeholder?: string;
    disabled?: boolean;
    autoFocus?: boolean;
    initialOrganizationScopedMessage?: boolean;
    hideOrganizationScopePicker?: boolean;
    /** Draft spreadsheet references to attach to the next message */
    draftRefs?: SpreadsheetReference[];
    /** Called when the user removes a draft ref chip */
    onRemoveDraftRef?: (index: number) => void;
    /** Draft transaction references to attach to the next message */
    draftTransactionRefs?: TransactionReference[];
    /** Called when the user removes a draft transaction ref chip */
    onRemoveDraftTransactionRef?: (index: number) => void;
    /** Draft counterparty references to attach to the next message */
    draftCounterpartyRefs?: CounterpartyReference[];
    /** Called when the user removes a draft counterparty ref chip */
    onRemoveDraftCounterpartyRef?: (index: number) => void;
    /** Draft invoice references to attach to the next message */
    draftInvoiceRefs?: InvoiceReference[];
    /** Called when the user removes a draft invoice ref chip */
    onRemoveDraftInvoiceRef?: (index: number) => void;
    /** Draft document references to attach to the next message */
    draftDocumentRefs?: DocumentReference[];
    /** Called when the user removes a draft document ref chip */
    onRemoveDraftDocumentRef?: (index: number) => void;
    /** Called when the user selects the /task slash command */
    onCreateTask?: () => void;
    /** Called whenever the composer content changes to non-empty text */
    onTyping?: () => void;
    /**
     * Identifier of the thread/subthread this composer belongs to. Changing it
     * resets staged attachments so files staged in one thread never leak into
     * another when the same `MessageForm` instance is reused across a switch.
     */
    resetKey?: string;
    /** Shows the drop highlight while dragged rows are over this composer */
    isDropTarget?: boolean;
    /** Lets the parent move focus into the composer (e.g. after a drop) */
    focusRef?: Ref<SlateEditorHandle>;
}

export const MessageForm: React.FC<MessageFormProps> = ({
    onSubmit,
    placeholder = "Leave a comment...",
    disabled,
    autoFocus,
    initialOrganizationScopedMessage,
    hideOrganizationScopePicker,
    draftRefs,
    onRemoveDraftRef,
    draftTransactionRefs,
    onRemoveDraftTransactionRef,
    draftCounterpartyRefs,
    onRemoveDraftCounterpartyRef,
    draftInvoiceRefs,
    onRemoveDraftInvoiceRef,
    draftDocumentRefs,
    onRemoveDraftDocumentRef,
    onCreateTask,
    onTyping,
    resetKey,
    isDropTarget,
    focusRef,
}) => {
    const { workspaceId } = useScope();
    const toaster = useToaster();
    const lastTypedValueRef = useRef<SlateValue | null>(null);
    const [pendingFiles, setPendingFiles] = useState<File[]>([]);
    const [isUploadingAttachments, setIsUploadingAttachments] = useState(false);
    // Caches document IDs for files that already uploaded so a retry after a
    // partial failure never re-uploads them (avoids orphaned duplicates).
    const uploadedDocumentIdsRef = useRef<Map<string, number[]>>(new Map());
    // Guards against setState / onSubmit firing after unmount mid-upload.
    const isMountedRef = useRef(true);
    // Synchronous in-flight guard: `isUploadingAttachments` is async state, so
    // two submits dispatched in the same tick could both pass it and double
    // upload/send. A ref flips synchronously to reject the re-entrant call.
    const isSubmittingRef = useRef(false);

    useEffect(
        () => () => {
            isMountedRef.current = false;
        },
        [],
    );

    useEffect(() => {
        setPendingFiles([]);
        uploadedDocumentIdsRef.current.clear();
    }, [resetKey]);

    const handleAddAttachments = useCallback(
        (files: File[]) => {
            const validFiles: File[] = [];
            for (const file of files) {
                if (!FINANCIAL_DOCUMENT_TYPES.includes(file.type)) {
                    toaster.error(`${file.name} - unsupported file type`);
                    continue;
                }
                if (file.size > MAX_FINANCIAL_DOCUMENT_FILE_SIZE_BYTES) {
                    toaster.error(
                        `${file.name} - Max allowed size is ${MAX_FINANCIAL_DOCUMENT_FILE_SIZE_MB}MB`,
                    );
                    continue;
                }
                validFiles.push(file);
            }
            if (validFiles.length === 0) {
                return;
            }
            setPendingFiles((prev) => {
                const seenKeys = new Set(prev.map(fileKey));
                const deduped: File[] = [];
                for (const file of validFiles) {
                    const key = fileKey(file);
                    if (seenKeys.has(key)) {
                        continue;
                    }
                    seenKeys.add(key);
                    deduped.push(file);
                }
                if (deduped.length === 0) {
                    return prev;
                }
                const next = [...prev, ...deduped];
                if (next.length > MAX_CHAT_ATTACHMENTS) {
                    toaster.error(
                        `You can attach up to ${MAX_CHAT_ATTACHMENTS} files per message`,
                    );
                }
                return next.slice(0, MAX_CHAT_ATTACHMENTS);
            });
        },
        [toaster],
    );

    const handleChange = useCallback(
        (value: SlateValue) => {
            // Slate keeps the same children reference for selection-only changes
            if (value === lastTypedValueRef.current) {
                return;
            }
            lastTypedValueRef.current = value;
            if (!isSlateValueEmpty(value)) {
                onTyping?.();
            }
        },
        [onTyping],
    );

    const handleRemoveAttachment = useCallback((index: number) => {
        setPendingFiles((prev) => {
            const removed = prev[index];
            if (removed) {
                uploadedDocumentIdsRef.current.delete(fileKey(removed));
            }
            return prev.filter((_, i) => i !== index);
        });
    }, []);

    const handleSubmit = useCallback(
        async (
            value: SlateValue,
            organizationId?: string | null,
        ): Promise<boolean> => {
            const hasFiles = pendingFiles.length > 0;
            if (
                (isSlateValueEmpty(value) && !hasFiles) ||
                disabled ||
                isUploadingAttachments ||
                isSubmittingRef.current
            ) {
                return false;
            }
            isSubmittingRef.current = true;

            const uploadAndSend = async (
                baseParams: MessageFormSubmitParams,
                uploadWorkspaceId: string,
            ): Promise<boolean> => {
                const uploadResults = await Promise.allSettled(
                    pendingFiles.map(async (file) => {
                        const key = fileKey(file);
                        const cached = uploadedDocumentIdsRef.current.get(key);
                        if (cached) {
                            return cached;
                        }
                        const content = await toBase64(file);
                        const document = await uploadDocument({
                            workspaceId: uploadWorkspaceId,
                            file: content,
                            filename: file.name,
                            contentType: file.type,
                        });
                        const documentIds = [document.id];
                        uploadedDocumentIdsRef.current.set(key, documentIds);
                        return documentIds;
                    }),
                );

                if (!isMountedRef.current) {
                    return false;
                }

                const hasFailure = uploadResults.some(
                    (result) => result.status === "rejected",
                );
                if (hasFailure) {
                    toaster.error(
                        "Failed to upload attachments. Please try again.",
                    );
                    return false;
                }

                const financialDocumentIds = uploadResults
                    .flatMap((result) =>
                        result.status === "fulfilled" ? result.value : [],
                    )
                    .filter((id, index, ids) => ids.indexOf(id) === index);
                try {
                    await onSubmit({ ...baseParams, financialDocumentIds });
                } catch {
                    // Send failed after uploads succeeded — keep the chips
                    // and the upload cache so retry reuses the already-
                    // uploaded documents instead of orphaning/re-uploading.
                    return false;
                }
                if (!isMountedRef.current) {
                    return false;
                }
                uploadedDocumentIdsRef.current.clear();
                setPendingFiles([]);
                return true;
            };

            const submit = async (): Promise<boolean> => {
                const baseParams: MessageFormSubmitParams = {
                    content: serializeSlateValue(value),
                    organizationId,
                    spreadsheetReferences:
                        draftRefs && draftRefs.length > 0
                            ? draftRefs
                            : undefined,
                    transactionReferences:
                        draftTransactionRefs && draftTransactionRefs.length > 0
                            ? draftTransactionRefs
                            : undefined,
                    counterpartyReferences:
                        draftCounterpartyRefs &&
                        draftCounterpartyRefs.length > 0
                            ? draftCounterpartyRefs
                            : undefined,
                    invoiceReferences:
                        draftInvoiceRefs && draftInvoiceRefs.length > 0
                            ? draftInvoiceRefs
                            : undefined,
                    documentReferences:
                        draftDocumentRefs && draftDocumentRefs.length > 0
                            ? draftDocumentRefs
                            : undefined,
                };

                if (!hasFiles) {
                    try {
                        await onSubmit(baseParams);
                        return true;
                    } catch {
                        // Send failed — preserve the typed content for retry.
                        return false;
                    }
                }

                if (!workspaceId) {
                    toaster.error("Select a workspace before attaching files");
                    return false;
                }

                // Upload attachments only on send, then pass document IDs along
                // with the message. Already-uploaded files are cached so a retry
                // after a partial failure re-uploads only the files that failed.
                setIsUploadingAttachments(true);
                return await uploadAndSend(baseParams, workspaceId).finally(
                    () => {
                        if (isMountedRef.current) {
                            setIsUploadingAttachments(false);
                        }
                    },
                );
            };

            return await submit().finally(() => {
                isSubmittingRef.current = false;
            });
        },
        [
            disabled,
            onSubmit,
            draftRefs,
            draftTransactionRefs,
            draftCounterpartyRefs,
            draftInvoiceRefs,
            draftDocumentRefs,
            pendingFiles,
            isUploadingAttachments,
            workspaceId,
            toaster,
        ],
    );

    return (
        <SlateEditor
            onChange={handleChange}
            onSubmit={handleSubmit}
            placeholder={placeholder}
            disabled={disabled}
            autoFocus={autoFocus}
            blurOnEscape
            initialOrganizationScopedMessage={initialOrganizationScopedMessage}
            hideOrganizationScopePicker={hideOrganizationScopePicker}
            draftRefs={draftRefs}
            onRemoveDraftRef={onRemoveDraftRef}
            draftTransactionRefs={draftTransactionRefs}
            onRemoveDraftTransactionRef={onRemoveDraftTransactionRef}
            draftCounterpartyRefs={draftCounterpartyRefs}
            onRemoveDraftCounterpartyRef={onRemoveDraftCounterpartyRef}
            draftInvoiceRefs={draftInvoiceRefs}
            onRemoveDraftInvoiceRef={onRemoveDraftInvoiceRef}
            draftDocumentRefs={draftDocumentRefs}
            onRemoveDraftDocumentRef={onRemoveDraftDocumentRef}
            onCreateTask={onCreateTask}
            pendingAttachments={pendingFiles}
            onAddAttachments={handleAddAttachments}
            onRemoveAttachment={handleRemoveAttachment}
            isUploadingAttachments={isUploadingAttachments}
            isDropTarget={isDropTarget}
            focusRef={focusRef}
        />
    );
};
